/**
 * Motion Variants - Framer Motion animation variants with custom easing
 * 
 * Defines reusable animation variants for headings, paragraphs, buttons, icons, and graphics.
 * Uses custom cubic-bezier easing curves for smooth, Stripe-like interactions.
 */

// Custom easing curves (Stripe-inspired)
export const easings = {
  smooth: [0.35, 0, 0.25, 1], // Smooth, natural feel
  snappy: [0.16, 1, 0.3, 1], // Quick, responsive
  gentle: [0.4, 0, 0.2, 1], // Gentle, flowing
  bounce: [0.68, -0.55, 0.265, 1.55], // Subtle bounce
};

// Check for reduced motion preference
const prefersReducedMotion = typeof window !== 'undefined'
  ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
  : false;

const duration = {
  fast: prefersReducedMotion ? 0.01 : 0.2,
  normal: prefersReducedMotion ? 0.01 : 0.6,
  slow: prefersReducedMotion ? 0.01 : 1,
};

/**
 * Heading variants - For main headings
 */
export const headingVariants = {
  hidden: {
    opacity: 0,
    y: 60,
    filter: 'blur(10px)',
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: duration.slow,
      ease: easings.smooth,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    filter: 'blur(5px)',
    transition: {
      duration: duration.fast,
      ease: easings.snappy,
    },
  },
};

/**
 * Text variants - For paragraphs and body text
 */
export const textVariants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.normal,
      ease: easings.gentle,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: duration.fast,
      ease: easings.snappy,
    },
  },
};

/**
 * Button variants - For CTA buttons
 */
export const buttonVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: duration.normal,
      ease: easings.snappy,
    },
  },
  hover: {
    scale: 1.05,
    y: -2,
    transition: {
      duration: duration.fast,
      ease: easings.snappy,
    },
  },
  tap: {
    scale: 0.95,
    transition: {
      duration: 0.1,
      ease: easings.snappy,
    },
  },
};

/**
 * Icon variants - For icons and graphics
 */
export const iconVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    rotate: -10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      duration: duration.normal,
      ease: easings.smooth,
    },
  },
  hover: {
    scale: 1.1,
    rotate: 5,
    transition: {
      duration: duration.fast,
      ease: easings.snappy,
    },
  },
};

/**
 * Stagger container - For coordinated child reveals
 */
export const staggerContainer = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: prefersReducedMotion ? 0 : 0.1,
      delayChildren: prefersReducedMotion ? 0 : 0.2,
      duration: duration.normal,
    },
  },
};

/**
 * Stagger item - Children of stagger containers
 */
export const staggerItem = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: duration.normal,
      ease: easings.smooth,
    },
  },
};

/**
 * Bobbing animation - Gentle vertical oscillation
 */
export const bobVariants = {
  animate: {
    y: [0, -5, 0],
    transition: {
      duration: prefersReducedMotion ? 0.01 : 2,
      repeat: Infinity,
      ease: easings.gentle,
      repeatType: 'reverse',
    },
  },
};

/**
 * GPU-accelerated transform helper
 */
export const gpuTransform = {
  transform: 'translate3d(0, 0, 0)',
  willChange: 'transform',
  backfaceVisibility: 'hidden',
  perspective: 1000,
};

